import { FileAction, FileState, useFileAPI } from 'components/common/FileAPI'
import { UploadType, getSignedURLRetrieverSeq } from 'components/common/FileAPI/fileUtils'
import { useEffect, useMemo, useState } from 'react'

import { AssignmentDocumentsEndpoint } from 'constants/API'
import { DocumentUploadType } from 'constants/documents'
import { SignedURLDTO } from 'models/visuals'
import constate from 'constate'
import { useAuth0 } from 'utils/auth'
import { useGetAssignmentDocuments } from 'dataQueries/assignmentDocuments.query'
import { useUserData } from 'components/contexts/UserDataContext'

export const [GalleryDocumentsContextProvider, useGalleryDocumentsContext] = constate(({ assignmentId }: { assignmentId: string }) => {


  const { roles } = useAuth0()
  const { userPropertyWorkspaceId } = useUserData()

  const galleryDocumentsFileController = useFileAPI('GALLERY_DOCUMENTS', {
    uploadUrlResolver: getSignedURLRetrieverSeq((fileEntry, api) => api.post<SignedURLDTO>(
      roles.isClient ? AssignmentDocumentsEndpoint.POST_UPLOAD_URL_WITH_WORKSPACE : AssignmentDocumentsEndpoint.POST_UPLOAD_URL,
      { assignmentId, workspaceId: userPropertyWorkspaceId },
      {
        filename: fileEntry.originalFilename,
        contentType: fileEntry.fileObject.type,
        type: fileEntry.tag,
      },
      true,
      {
        params: {
          uploadType: UploadType.RESUMABLE
        }
      }
    )),
    reUploadUrlResolver: (fileEntry, api) => api.put<SignedURLDTO>(
      roles.isClient ? AssignmentDocumentsEndpoint.POST_REUPLOAD_URL_WITH_WORKSPACE : AssignmentDocumentsEndpoint.POST_REUPLOAD_URL,
      { assignmentId, workspaceId: userPropertyWorkspaceId },
      {
        filename: fileEntry.gcFilename,
        contentType: fileEntry.fileObject.type,
        replacementFilename: fileEntry.fileObject.name,
        type: fileEntry.tag,
      },
      true
    ),
    deleteHandler: (fileEntry, api) => api.delete(
      roles.isClient ? AssignmentDocumentsEndpoint.DELETE_DOCUMENT_WITH_WORKSPACE : AssignmentDocumentsEndpoint.DELETE_DOCUMENT,
      { assignmentId, workspaceId: userPropertyWorkspaceId },
      true,
      {
        params: {
          type: fileEntry.tag,
          filename: fileEntry.gcFilename,
        }
      }
    )
  })

  const [documentTypeSelect, setDocumentTypeSelect] = useState<DocumentUploadType>(roles.isCreative ? DocumentUploadType.OUTPUT : DocumentUploadType.INPUT)
  const [isMessageThreadsVisible, setIsMessageThreadsVisible] = useState<boolean>(false)

  const assignmentDocuments = useGetAssignmentDocuments(assignmentId, documentTypeSelect, userPropertyWorkspaceId, roles)
  const assignmentDocumentsData = useMemo(() => assignmentDocuments.data?.data || [], [assignmentDocuments.data?.data])

  useEffect(() => {
    assignmentDocuments.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [documentTypeSelect])

  useEffect(() => {
    galleryDocumentsFileController.mergeAddFiles(assignmentDocumentsData.map((file) => ({
      id: file.signedUrl.filename,
      displayUrl: file.signedUrl.signedURL,
      originalFilename: file.originalName,
      gcFilename: file.signedUrl.filename,
      tag: documentTypeSelect,
      action: FileAction.LOAD,
      state: FileState.SUCCESS,
      progress: 100,
    })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentDocumentsData])

  return {
    documentTypeSelect,
    setDocumentTypeSelect,
    assignmentDocumentsData,
    assignmentDocuments,
    galleryDocumentsFileController,
    isMessageThreadsVisible,
    setIsMessageThreadsVisible,
  }
})
